"use client";
import { motion } from "framer-motion";
import Link from "next/link";

const sections = [
  {
    title: "1. Introduction & Overview",
    content: `LINDY Balinese Cats ("LINDY", "we", "us", or "our") operates as a premium Balinese cat breeding and adoption service. This Privacy Policy explains how we collect, use, process, and protect your personal information when you visit our website, engage with our services, or participate in our adoption process. We are committed to transparency and protecting your privacy rights in accordance with applicable laws including GDPR, CCPA, and other privacy regulations.`
  },
  {
    title: "2. Information We Collect",
    points: [
      "**Personal Identifiers**: Full name, title, contact details (email, phone, address), date of birth, and identification documents when required for adoption contracts.",
      "**Household & Lifestyle Data**: Family composition, home environment details, work schedules, travel patterns, pet ownership history, and preferences for communication and placement.",
      "**Financial Information**: Payment details, billing addresses, and transaction records processed through secure third-party payment processors (we do not store complete payment card information).",
      "**Communication Records**: Email correspondence, chat logs, consultation notes, and feedback provided during the adoption process.",
      "**Technical Data**: IP address, browser type, device information, cookies, and usage analytics to improve our website experience and services.",
      "**Health & Veterinary Records**: Vaccination records, health certificates, and medical history provided by veterinarians or shared for placement coordination.",
      "**Media & Documentation**: Photographs, videos, testimonials, and other media shared during the adoption process or for marketing purposes with explicit consent."
    ]
  },
  {
    title: "3. How We Collect Information",
    points: [
      "**Direct Collection**: Information provided through contact forms, waiting list applications, consultation bookings, and adoption applications.",
      "**Automated Collection**: Technical data collected through cookies, analytics tools, and website interaction tracking.",
      "**Third-Party Sources**: Information from veterinarians, references, or service providers involved in the adoption process.",
      "**Communication Channels**: Data collected through emails, phone calls, social media, and in-person consultations."
    ]
  },
  {
    title: "4. Legal Basis for Processing (GDPR)",
    content: `For visitors and clients in the European Union, we process personal data based on the following lawful grounds:

• **Contract**: Processing necessary to fulfill adoption agreements and provide services
• **Legitimate Interest**: Improving services, ensuring security, and direct marketing of similar services
• **Consent**: For marketing communications and non-essential cookies
• **Legal Obligation**: Compliance with veterinary regulations, consumer protection laws, and financial reporting requirements
• **Vital Interest**: Protecting the health and welfare of animals and people` },
  {
    title: "5. How We Use Your Information",
    points: [
      "**Adoption Services**: Matching kittens with suitable guardians, conducting consultations, and facilitating the adoption process.",
      "**Communication**: Sending updates about available kittens, litter announcements, care instructions, and follow-up support.",
      "**Customer Service**: Responding to inquiries, providing support, and managing customer relationships.",
      "**Legal Compliance**: Maintaining records for regulatory compliance, veterinary requirements, and dispute resolution.",
      "**Service Improvement**: Analyzing website usage, customer feedback, and service performance to enhance our offerings.",
      "**Marketing**: With explicit consent, sending newsletters, promotional materials, and information about events or services.",
      "**Security & Fraud Prevention**: Protecting against unauthorized access, fraud, and ensuring platform security."
    ]
  },
  {
    title: "6. Information Sharing & Disclosure",
    content: `We do not sell, rent, or trade your personal information. We may share limited information only in the following circumstances:

• **Service Providers**: With veterinarians, transporters, payment processors, and other third parties essential to adoption services
• **Legal Requirements**: When required by law, court order, or regulatory authorities
• **Business Transfers**: In connection with mergers, acquisitions, or asset sales (with notice to affected users)
• **Consent**: With your explicit permission for specific purposes
• **Vital Interests**: To protect health, safety, or property when necessary

All third-party service providers are contractually bound to maintain confidentiality and security standards comparable to ours.` },
  {
    title: "7. Cookies & Tracking Technologies",
    content: `We use cookies and similar technologies to enhance your browsing experience:

**Essential Cookies**: Required for website functionality, security, and basic operations
**Analytics Cookies**: Google Analytics and similar tools to understand website usage and improve services
**Marketing Cookies**: To deliver relevant advertisements and measure campaign effectiveness
**Preference Cookies**: To remember your settings and personalize your experience

You can manage cookie preferences through your browser settings or our cookie consent banner. Note that disabling certain cookies may affect website functionality.` },
  {
    title: "8. Data Retention",
    content: `We retain personal information only as long as necessary for the purposes outlined:

• **Active Clients**: Duration of adoption process plus 7 years for legal and warranty purposes
• **Waiting List**: Up to 3 years unless renewed or converted to active status
• **Website Visitors**: Technical data retained for 2 years for analytics and security
• **Marketing Contacts**: Until consent is withdrawn or data becomes inactive (3 years maximum)
• **Legal Requirements**: As required by applicable laws and regulations

When retention periods expire, data is securely deleted, anonymized, or archived as required by law.` },
  {
    title: "9. Your Rights & Choices",
    points: [
      "**Access**: Request copies of personal data we hold about you.",
      "**Rectification**: Correct inaccurate or incomplete personal information.",
      "**Erasure**: Request deletion of personal data in certain circumstances (\"right to be forgotten\").",
      "**Portability**: Receive your data in a structured, commonly used format.",
      "**Restriction**: Limit how we process your personal data in certain cases.",
      "**Objection**: Object to processing based on legitimate interests or direct marketing.",
      "**Withdrawal**: Withdraw consent for processing that requires consent.",
      "**Complaint**: Lodge complaints with supervisory authorities regarding data processing."
    ]
  },
  {
    title: "10. California Privacy Rights (CCPA)",
    content: `California residents have additional rights under the California Consumer Privacy Act:

• **Right to Know**: Information about categories of personal data collected and purposes of processing
• **Right to Delete**: Request deletion of personal information (subject to legal exceptions)
• **Right to Opt-Out**: Opt-out of sale of personal information (we do not sell data)
• **Right to Non-Discrimination**: Receive equal service regardless of exercising privacy rights
• **Right to Correct**: Request correction of inaccurate personal information

To exercise these rights, contact us using the information provided below. We will respond within 45 days as required by law.` },
  {
    title: "11. International Data Transfers",
    content: `LINDY operates primarily in the United States but serves clients worldwide. When we transfer personal data internationally:

• **Adequacy Decisions**: We transfer data to countries deemed adequate by the European Commission
• **Standard Contractual Clauses**: We use approved contractual clauses for transfers to other countries
• **Your Consent**: We obtain explicit consent for certain international transfers
• **Derogations**: We rely on appropriate legal exceptions when applicable

We ensure all international transfers maintain the same level of protection as required by applicable laws.` },
  {
    title: "12. Data Security",
    content: `We implement comprehensive security measures to protect your personal information:

• **Technical Safeguards**: Encryption, secure servers, firewalls, and access controls
• **Administrative Controls**: Employee training, access restrictions, and security policies
• **Physical Security**: Secure facilities and device protection measures
• **Regular Audits**: Security assessments, vulnerability testing, and compliance reviews
• **Incident Response**: Procedures for data breach detection, investigation, and notification

While we strive for the highest security standards, no system is completely immune to risks. We encourage you to protect your account credentials and notify us of any suspected security issues.` },
  {
    title: "13. Children's Privacy",
    content: `LINDY services are intended for adults aged 18 and over. We do not knowingly collect personal information from children under 13 without parental consent. If we become aware of such collection, we will promptly delete the information and take appropriate measures.` },
  {
    title: "14. Third-Party Links",
    content: `Our website may contain links to third-party websites, social media platforms, or services. We are not responsible for the privacy practices of these external sites. We encourage you to review the privacy policies of any third-party services you use.` },
  {
    title: "15. Policy Updates",
    content: `We may update this Privacy Policy periodically to reflect changes in our practices, technology, or legal requirements. When we make material changes:

• We will notify you via email (if we have your email address) or prominent website notice
• Updates will be posted with a revised "Last Updated" date
• Your continued use of our services after updates constitutes acceptance of the revised policy
• We recommend reviewing this policy regularly to stay informed of your privacy rights` },
  {
    title: "16. Contact Information & Data Protection Officer",
    content: `For privacy-related questions, to exercise your rights, or to file complaints:

**Data Protection Officer Contact**:
Email: privacy@lindybalinesecats.com
Phone: +1 (606) 465-2993

**Mailing Address**:
LINDY Balinese Cats
Attention: Data Protection Officer
[Your Address - To be provided]

**Response Times**:
• General inquiries: 5-7 business days
• Rights requests: 30 days (45 days for CCPA requests)
• Complaints: Acknowledged within 48 hours

**Supervisory Authorities**:
For GDPR complaints, you may contact your local data protection authority or the Oregon Attorney General for US matters.` }
];

export default function PrivacyPage() {
  return (
    <main className="min-h-screen bg-gradient-to-br from-cream via-sand/20 to-soft-gold/30">
      <div className="max-w-5xl mx-auto px-6 py-16 space-y-16">
        <motion.section
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <p className="text-sm uppercase tracking-[0.3em] text-soft-gold mb-4">Privacy Policy</p>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-warm-brown mb-6">
            LINDY Privacy & Data Protection Charter
          </h1>
          <p className="text-xl text-warm-brown/70 max-w-3xl mx-auto">
            Our comprehensive privacy commitment ensures transparency, compliance with GDPR and CCPA, and robust protection of your personal information throughout your adoption journey.
          </p>
        </motion.section>

        <motion.section
          className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-8 md:p-12 space-y-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          {sections.map((section, index) => (
            <div key={section.title} className="space-y-4">
              <h2 className="font-heading text-2xl text-warm-brown flex items-center gap-3">
                <span className="text-soft-gold text-lg">{String(index + 1).padStart(2, '0')}</span>
                {section.title}
              </h2>
              {section.content && (
                <p className="text-warm-brown/75 leading-relaxed">
                  {section.content}
                </p>
              )}
              {section.points && (
                <ul className="space-y-2 text-warm-brown/75 leading-relaxed">
                  {section.points.map((point) => (
                    <li key={point} className="flex items-start gap-3">
                      <span className="mt-1 text-soft-gold">•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </motion.section>

        <motion.section
          className="bg-gradient-to-r from-soft-gold/10 to-cream rounded-3xl p-10 shadow-xl text-center space-y-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h2 className="font-heading text-3xl text-warm-brown mb-2">Questions about your privacy rights?</h2>
          <p className="text-warm-brown/70 max-w-2xl mx-auto">
            Our comprehensive privacy framework ensures GDPR and CCPA compliance with clear procedures for exercising your data rights. Contact our Data Protection Officer for assistance with access requests, corrections, or withdrawal of consent.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/contact"
              className="px-6 py-3 bg-soft-gold text-white rounded-xl shadow-lg hover:shadow-xl transition-all font-medium"
            >
              Contact Privacy Officer
            </Link>
            <Link
              href="/terms"
              className="px-6 py-3 border border-soft-gold text-soft-gold rounded-xl hover:bg-soft-gold hover:text-white transition-all font-medium"
            >
              Review Terms of Service
            </Link>
          </div>
        </motion.section>
      </div>
    </main>
  );
}
